// ─────────────────────────────────────────────────────────────────────────────
// Amend a soft-locked invoice (open / partially_paid) without un-completing the
// booking. All edits are STAGED locally; nothing is written until Save, which
// sends one atomic RPC (amend_booking_invoice) that updates the booking tables,
// rebuilds the invoice, and re-issues it under the same number.
//
// Layout + behavior mirror the booking info modal:
//   • services grouped per vehicle in white cards with gray headers
//   • service descriptions (RichTextDescription), add-ons under a divider
//   • per-line service discounts with the same $/% editor and price display
//     (net price bold, original struck through, green −10% / −$25 label)
//   • services are added through the same SelectServiceModal
//   • products only appear under the booking info modal's criteria
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Trash2, Loader2, FileEdit, Package2 } from 'lucide-react';
import { useModalZIndex } from '../../contexts/ModalStackContext';
import { useAppSystem } from '../../hooks/useAppSystem';
import { supabase } from '../../lib/supabase';
import RichTextDescription from '../RichTextDescription';
import SelectServiceModal from '../manage/SelectServiceModal';
import {
  fetchAmendableBilling,
  fetchAmendCatalogs,
  amendBookingInvoice,
  type AmendableBilling,
  type AmendChanges,
} from '../../lib/invoices/amendInvoice';

interface AmendInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookingId: string;
  businessId: string;
  /** Called after a successful amend so the parent refetches invoice + booking. */
  onAmended: () => void;
}

interface StagedAddon {
  id: string;
  name: string;
  price: number;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  deleted: boolean;
}

interface StagedService {
  id: string;
  name: string;
  description: string | null;
  price: number;
  quantity: number;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  addons: StagedAddon[];
  deleted: boolean;
  vehicle_id: string | null;
  vehicle_label: string;
  vehicle_color: string | null;
}

interface StagedNewService {
  key: string;
  service_id: string;
  name: string;
  description: string;
  price: number;
  quantity: number;
  vehicle_id: string | null;
  tax_exempt: boolean;
  tax_rate: number;
  pricing_type: string;
  unit_price?: number;
  duration_per_unit?: number;
  duration_minutes?: number;
}

interface StagedProduct {
  id: string;
  stock_item_id: string;
  name: string;
  quantity: number;
  unit_price: number;
  deleted: boolean;
}

interface StagedNewProduct {
  key: string;
  stock_item_id: string;
  name: string;
  quantity: number;
  unit_price: number;
}

interface StagedDiscount {
  type: 'percentage' | 'fixed';
  amount: number;
  title: string;
}

const money = (v: number) => `$${v.toFixed(2)}`;
const money2 = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Resolved dollar discount for a line — same math as the booking info modal. */
function resolvedLineDiscount(price: number, type: 'percentage' | 'fixed', value: number): number {
  if (value <= 0) return 0;
  const raw = type === 'percentage' ? (price * value) / 100 : value;
  return Math.round(Math.min(Math.max(raw, 0), price) * 100) / 100;
}

const appleFont = {
  fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
  letterSpacing: '-0.01em',
} as const;

export default function AmendInvoiceModal({
  isOpen,
  onClose,
  bookingId,
  businessId,
  onAmended,
}: AmendInvoiceModalProps) {
  // Stacks above whichever modal opened us (payments modal, booking modal)
  // via the app's existing ModalStackProvider — no hardcoded z values.
  const zIndex = useModalZIndex(1300);
  const { coreApps, installedAddons } = useAppSystem();
  const isInventoryAppInstalled =
    coreApps.some((app) => app.app?.slug === 'inventory') ||
    installedAddons.some((app) => app.app?.slug === 'inventory');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [billing, setBilling] = useState<AmendableBilling | null>(null);

  const [services, setServices] = useState<StagedService[]>([]);
  const [newServices, setNewServices] = useState<StagedNewService[]>([]);
  const [products, setProducts] = useState<StagedProduct[]>([]);
  const [newProducts, setNewProducts] = useState<StagedNewProduct[]>([]);
  const [discount, setDiscount] = useState<StagedDiscount | null>(null);
  const [discountDirty, setDiscountDirty] = useState(false);
  const [reason, setReason] = useState('');

  const [productCatalog, setProductCatalog] = useState<{ id: string; name: string; price: number }[]>([]);
  const [isSelectServiceOpen, setIsSelectServiceOpen] = useState(false);
  const [showAddProduct, setShowAddProduct] = useState(false);

  // Per-line discount editor — which service (by id/key) has its editor open,
  // and the draft type/value inside it (same UX as ModifyBookingService).
  const [discountEditorFor, setDiscountEditorFor] = useState<string | null>(null);
  const [lineDiscountType, setLineDiscountType] = useState<'percentage' | 'fixed'>('fixed');
  const [lineDiscountValue, setLineDiscountValue] = useState('');

  // Same criteria as the booking info modal's Products section.
  const [showInventoryInBookings, setShowInventoryInBookings] = useState(true);
  useEffect(() => {
    if (!isOpen || !businessId || !isInventoryAppInstalled) return;
    supabase
      .rpc('get_app_setting', {
        p_business_id: businessId,
        p_app_slug: 'inventory',
        p_setting_key: 'show_inventory_in_bookings',
        p_default_value: true,
      })
      .then(({ data, error: settingErr }) => {
        if (!settingErr && data !== null) setShowInventoryInBookings(data as boolean);
      });
  }, [isOpen, businessId, isInventoryAppInstalled]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [data, catalogs] = await Promise.all([
        fetchAmendableBilling(bookingId),
        fetchAmendCatalogs(businessId),
      ]);
      setBilling(data);
      setServices(
        data.services.map((s) => ({
          id: s.id,
          name: s.name,
          description: s.description,
          price: s.price,
          quantity: s.quantity,
          discount_type: s.discount_type,
          discount_value: s.discount_value,
          addons: s.addons.map((a) => ({ ...a, deleted: false })),
          deleted: false,
          vehicle_id: s.vehicle_id,
          vehicle_label: s.vehicle_label,
          vehicle_color: s.vehicle_color,
        })),
      );
      setProducts(
        data.products.map((p) => ({
          id: p.id,
          stock_item_id: p.stock_item_id,
          name: p.product_name,
          quantity: p.quantity,
          unit_price: p.unit_price,
          deleted: false,
        })),
      );
      setDiscount(data.discount ? { ...data.discount } : null);
      setDiscountDirty(false);
      setNewServices([]);
      setNewProducts([]);
      setReason('');
      setDiscountEditorFor(null);
      setProductCatalog(catalogs.products);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load invoice');
    } finally {
      setLoading(false);
    }
  }, [bookingId, businessId]);

  useEffect(() => {
    if (isOpen) void load();
  }, [isOpen, load]);

  // Products section is relevant under the booking info modal's criteria — but
  // always shown when the booking already has product lines (they must stay amendable).
  const productsRelevant =
    (isInventoryAppInstalled && showInventoryInBookings) || products.length > 0 || newProducts.length > 0;

  // Group services by vehicle, mirroring BookingServiceList.
  const serviceGroups = useMemo(() => {
    const groups = new Map<string, { label: string; color: string | null; items: StagedService[] }>();
    for (const s of services) {
      if (s.deleted) continue;
      const key = s.vehicle_id || 'no-vehicle';
      if (!groups.has(key)) {
        groups.set(key, { label: s.vehicle_label, color: s.vehicle_color, items: [] });
      }
      groups.get(key)!.items.push(s);
    }
    return [...groups.entries()].map(([key, g]) => ({ key, ...g }));
  }, [services]);

  // New services attach to the booking's vehicle when it unambiguously has one.
  const singleVehicleId = useMemo(() => {
    const ids = new Set(services.map((s) => s.vehicle_id).filter(Boolean));
    return ids.size === 1 ? [...ids][0] : null;
  }, [services]);

  // ── Estimated new subtotal (pre-tax; tax is recomputed server-side).
  //    Line discounts are applied per line, then the booking-level discount. ──
  const estimatedSubtotal = useMemo(() => {
    let sum = 0;
    for (const s of services) {
      if (s.deleted) continue;
      sum += Math.max(s.price - resolvedLineDiscount(s.price, s.discount_type, s.discount_value), 0);
      for (const a of s.addons) {
        if (a.deleted) continue;
        sum += Math.max(a.price - resolvedLineDiscount(a.price, a.discount_type, a.discount_value), 0);
      }
    }
    for (const s of newServices) sum += s.price;
    for (const p of products) if (!p.deleted) sum += p.unit_price * p.quantity;
    for (const p of newProducts) sum += p.unit_price * p.quantity;

    if (discount) {
      sum -= discount.type === 'percentage' ? (sum * discount.amount) / 100 : discount.amount;
    }
    return Math.max(sum, 0);
  }, [services, newServices, products, newProducts, discount]);

  const paidSoFar = (billing?.invoice.amount_paid_cents ?? 0) / 100;
  const currentTotal = (billing?.invoice.total_cents ?? 0) / 100;

  const hasChanges = useMemo(() => {
    if (!billing) return false;
    if (newServices.length > 0 || newProducts.length > 0 || discountDirty) return true;
    for (const s of services) {
      const orig = billing.services.find((o) => o.id === s.id);
      if (!orig) continue;
      if (
        s.deleted ||
        s.price !== orig.price ||
        s.quantity !== orig.quantity ||
        s.discount_type !== orig.discount_type ||
        s.discount_value !== orig.discount_value
      ) {
        return true;
      }
      for (const a of s.addons) {
        const origA = orig.addons.find((o) => o.id === a.id);
        if (!origA) continue;
        if (a.deleted || a.price !== origA.price) return true;
      }
    }
    for (const p of products) {
      const orig = billing.products.find((o) => o.id === p.id);
      if (!orig) continue;
      if (p.deleted || p.quantity !== orig.quantity || p.unit_price !== orig.unit_price) return true;
    }
    return false;
  }, [billing, services, newServices, products, newProducts, discountDirty]);

  const buildChanges = (): AmendChanges => {
    if (!billing) return {};
    const changes: AmendChanges = {};

    const serviceUpdates: NonNullable<AmendChanges['service_updates']> = [];
    const serviceDeletes: string[] = [];
    const addonUpdates: NonNullable<AmendChanges['addon_updates']> = [];
    const addonDeletes: string[] = [];

    for (const s of services) {
      const orig = billing.services.find((o) => o.id === s.id);
      if (!orig) continue;
      if (s.deleted) {
        serviceDeletes.push(s.id);
        continue;
      }
      const priceChanged = s.price !== orig.price || s.quantity !== orig.quantity;
      const discountChanged = s.discount_type !== orig.discount_type || s.discount_value !== orig.discount_value;
      if (priceChanged || discountChanged) {
        serviceUpdates.push({
          id: s.id,
          price: s.price,
          quantity: s.quantity,
          // Discount is re-resolved server-side against the (possibly new) price
          ...(discountChanged || (priceChanged && s.discount_value > 0)
            ? { discount_type: s.discount_type, discount_value: s.discount_value }
            : {}),
        });
      }
      for (const a of s.addons) {
        const origA = orig.addons.find((o) => o.id === a.id);
        if (!origA) continue;
        if (a.deleted) addonDeletes.push(a.id);
        else if (a.price !== origA.price) addonUpdates.push({ id: a.id, price: a.price });
      }
    }

    const productUpdates: NonNullable<AmendChanges['product_updates']> = [];
    const productDeletes: string[] = [];
    for (const p of products) {
      const orig = billing.products.find((o) => o.id === p.id);
      if (!orig) continue;
      if (p.deleted) productDeletes.push(p.id);
      else if (p.quantity !== orig.quantity || p.unit_price !== orig.unit_price) {
        productUpdates.push({ id: p.id, quantity: p.quantity, unit_price: p.unit_price });
      }
    }

    if (serviceUpdates.length) changes.service_updates = serviceUpdates;
    if (serviceDeletes.length) changes.service_deletes = serviceDeletes;
    if (newServices.length) {
      changes.service_adds = newServices.map((s) => ({
        service_id: s.service_id,
        price: s.price,
        quantity: s.quantity,
        name: s.name,
        description: s.description,
        vehicle_id: s.vehicle_id,
        tax_exempt: s.tax_exempt,
        tax_rate: s.tax_rate,
        pricing_type: s.pricing_type,
        unit_price: s.unit_price,
        duration_per_unit: s.duration_per_unit,
        duration_minutes: s.duration_minutes,
      }));
    }
    if (addonUpdates.length) changes.addon_updates = addonUpdates;
    if (addonDeletes.length) changes.addon_deletes = addonDeletes;
    if (productUpdates.length) changes.product_updates = productUpdates;
    if (productDeletes.length) changes.product_deletes = productDeletes;
    if (newProducts.length) {
      changes.product_adds = newProducts.map((p) => ({
        stock_item_id: p.stock_item_id,
        quantity: p.quantity,
        unit_price: p.unit_price,
      }));
    }
    if (discountDirty) {
      changes.discount = discount
        ? { action: 'set', type: discount.type, amount: discount.amount, title: discount.title }
        : { action: 'remove' };
    }
    return changes;
  };

  const handleSave = async () => {
    if (!hasChanges || saving) return;
    setSaving(true);
    setError(null);
    try {
      await amendBookingInvoice(bookingId, buildChanges(), reason || undefined);
      onAmended();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to amend invoice');
    } finally {
      setSaving(false);
    }
  };

  const openDiscountEditor = (s: StagedService) => {
    setDiscountEditorFor(s.id);
    setLineDiscountType(s.discount_value > 0 ? s.discount_type : 'fixed');
    setLineDiscountValue(s.discount_value > 0 ? String(s.discount_value) : '');
  };

  const applyLineDiscount = (serviceId: string) => {
    const val = Math.max(parseFloat(lineDiscountValue) || 0, 0);
    setServices((prev) =>
      prev.map((x) =>
        x.id === serviceId ? { ...x, discount_type: lineDiscountType, discount_value: val } : x,
      ),
    );
    setDiscountEditorFor(null);
  };

  const clearLineDiscount = (serviceId: string) => {
    setServices((prev) =>
      prev.map((x) => (x.id === serviceId ? { ...x, discount_type: 'fixed', discount_value: 0 } : x)),
    );
    setDiscountEditorFor(null);
  };

  if (!isOpen) return null;

  const smallPriceInput =
    'w-[74px] text-right text-[13px] font-medium text-gray-900 bg-gray-50 border border-gray-200 rounded-[8px] px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500';

  return createPortal(
    <>
    <div
      className="fixed inset-0 bg-black/40"
      style={{ zIndex }}
      onClick={onClose}
    >
      {/* Right-side mounted panel (same convention as the payments modal) */}
      <div
        className="absolute right-0 top-0 h-full bg-gray-50 flex flex-col w-full sm:w-[520px]"
        style={{ boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.15)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="bg-white flex items-center justify-between px-5 pb-3 border-b border-gray-100 shrink-0"
          style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}
        >
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-amber-50">
              <FileEdit className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-[17px] font-semibold text-gray-900 leading-tight" style={appleFont}>
                Amend Invoice{billing?.invoice.invoice_number ? ` #${billing.invoice.invoice_number}` : ''}
              </h2>
              <p className="text-[11px] text-gray-500 mt-0.5">
                Changes update the booking and re-issue this invoice under the same number.
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full active:bg-gray-100 touch-manipulation">
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <>
              {/* ── Services (grouped by vehicle, like the booking info modal) ── */}
              {serviceGroups.map((group) => (
                <div
                  key={group.key}
                  className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden"
                >
                  {group.label && (
                    <div className="bg-gray-50/50 px-3 py-2.5 border-b border-gray-200/60" style={appleFont}>
                      <div className="text-[13px] font-semibold text-gray-900 truncate">{group.label}</div>
                      {group.color && <div className="text-[11px] text-gray-600 mt-0.5">{group.color}</div>}
                    </div>
                  )}
                  <div className="divide-y divide-gray-200/60">
                    {group.items.map((s) => {
                      const lineDiscount = resolvedLineDiscount(s.price, s.discount_type, s.discount_value);
                      const hasLineDiscount = lineDiscount > 0;
                      const netPrice = Math.max(s.price - lineDiscount, 0);
                      const discountLabel = hasLineDiscount
                        ? s.discount_type === 'percentage'
                          ? `−${s.discount_value}%`
                          : `−$${money2(lineDiscount)}`
                        : null;
                      return (
                        <div key={s.id} className="px-3 py-2">
                          <div className="flex items-start justify-between py-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-[14px] font-semibold text-gray-900" style={appleFont}>
                                  {s.name}
                                </span>
                              </div>
                              {s.description && (
                                <RichTextDescription
                                  description={s.description}
                                  className="text-[12px] text-gray-600 mb-1.5 leading-relaxed"
                                />
                              )}

                              {/* Action pills — same as the booking info modal */}
                              <div className="flex items-center space-x-2 mt-2.5">
                                <button
                                  onClick={() =>
                                    discountEditorFor === s.id ? setDiscountEditorFor(null) : openDiscountEditor(s)
                                  }
                                  className={`px-3 py-1.5 rounded-[8px] text-[11px] font-medium active:scale-95 transition-all duration-150 touch-manipulation ${
                                    discountLabel
                                      ? 'bg-green-50 text-green-700 active:bg-green-100'
                                      : 'bg-gray-50 text-gray-700 active:bg-gray-100'
                                  }`}
                                  style={{ WebkitTapHighlightColor: 'transparent' }}
                                >
                                  Discount
                                </button>
                                <button
                                  onClick={() =>
                                    setServices((prev) =>
                                      prev.map((x) => (x.id === s.id ? { ...x, deleted: true } : x)),
                                    )
                                  }
                                  className="px-3 py-1.5 bg-red-50 text-red-600 rounded-[8px] text-[11px] font-medium active:bg-red-200 active:scale-95 transition-all duration-150 touch-manipulation"
                                  style={{ WebkitTapHighlightColor: 'transparent' }}
                                >
                                  Remove
                                </button>
                              </div>

                              {/* Line discount editor — same $/% control as the booking modal */}
                              {discountEditorFor === s.id && (
                                <div className="mt-2 p-2.5 bg-gray-50 border border-gray-200 rounded-[10px]">
                                  <div className="flex items-center gap-2">
                                    <div className="flex bg-white border border-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                                      <button
                                        type="button"
                                        onClick={() => setLineDiscountType('fixed')}
                                        className={`px-2.5 py-1.5 text-[12px] font-semibold ${lineDiscountType === 'fixed' ? 'bg-blue-600 text-white' : 'text-gray-600'}`}
                                      >
                                        $
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => setLineDiscountType('percentage')}
                                        className={`px-2.5 py-1.5 text-[12px] font-semibold ${lineDiscountType === 'percentage' ? 'bg-blue-600 text-white' : 'text-gray-600'}`}
                                      >
                                        %
                                      </button>
                                    </div>
                                    <input
                                      type="number"
                                      inputMode="decimal"
                                      min="0"
                                      value={lineDiscountValue}
                                      onChange={(e) => setLineDiscountValue(e.target.value)}
                                      placeholder={lineDiscountType === 'percentage' ? 'e.g. 10' : 'e.g. 25.00'}
                                      className="flex-1 min-w-0 px-2.5 py-1.5 text-[13px] bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                                      style={{ fontSize: '16px' }}
                                    />
                                    <button
                                      onClick={() => applyLineDiscount(s.id)}
                                      className="px-3 py-1.5 bg-blue-600 text-white rounded-[8px] text-[12px] font-semibold active:scale-95 transition-all touch-manipulation"
                                      style={{ WebkitTapHighlightColor: 'transparent' }}
                                    >
                                      Apply
                                    </button>
                                    {hasLineDiscount && (
                                      <button
                                        onClick={() => clearLineDiscount(s.id)}
                                        className="h-7 w-7 flex items-center justify-center text-red-500 bg-red-50 active:bg-red-100 rounded-full active:scale-95 transition-all flex-shrink-0"
                                        style={{ WebkitTapHighlightColor: 'transparent' }}
                                      >
                                        <Trash2 className="h-3.5 w-3.5" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Price — editable, with the booking modal's discount display */}
                            <div className="ml-3 flex-shrink-0 text-right" style={appleFont}>
                              <input
                                type="number"
                                inputMode="decimal"
                                className="w-[84px] text-right text-[15px] font-semibold text-gray-900 bg-gray-50 border border-gray-200 rounded-[8px] px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                                value={s.price}
                                min={0}
                                step="0.01"
                                onChange={(e) =>
                                  setServices((prev) =>
                                    prev.map((x) =>
                                      x.id === s.id ? { ...x, price: Number(e.target.value) || 0 } : x,
                                    ),
                                  )
                                }
                              />
                              {hasLineDiscount && (
                                <div className="flex items-center justify-end gap-1.5 mt-1">
                                  <span className="text-[11px] font-medium text-green-600">{discountLabel}</span>
                                  <span className="text-[12px] font-semibold text-gray-900">
                                    ${money2(netPrice)}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Add-ons — same divider + label treatment as the booking modal */}
                          {s.addons.filter((a) => !a.deleted).length > 0 && (
                            <div className="mt-1 pt-2 border-t border-gray-200/60">
                              <div className="text-[11px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">
                                Add-ons
                              </div>
                              <div className="space-y-1.5 pb-1">
                                {s.addons.filter((a) => !a.deleted).map((a) => {
                                  const addonDiscount = resolvedLineDiscount(a.price, a.discount_type, a.discount_value);
                                  const addonLabel = addonDiscount > 0
                                    ? a.discount_type === 'percentage'
                                      ? `−${a.discount_value}%`
                                      : `−$${money2(addonDiscount)}`
                                    : null;
                                  return (
                                    <div key={a.id} className="flex items-center justify-between gap-2">
                                      <span className="text-[12px] text-gray-700 flex-1 truncate">
                                        {a.name}
                                        {addonLabel && (
                                          <span className="ml-1.5 text-[11px] font-medium text-green-600">{addonLabel}</span>
                                        )}
                                      </span>
                                      <input
                                        type="number"
                                        inputMode="decimal"
                                        className={smallPriceInput}
                                        value={a.price}
                                        min={0}
                                        step="0.01"
                                        onChange={(e) =>
                                          setServices((prev) =>
                                            prev.map((x) =>
                                              x.id === s.id
                                                ? {
                                                    ...x,
                                                    addons: x.addons.map((y) =>
                                                      y.id === a.id
                                                        ? { ...y, price: Number(e.target.value) || 0 }
                                                        : y,
                                                    ),
                                                  }
                                                : x,
                                            ),
                                          )
                                        }
                                      />
                                      <button
                                        onClick={() =>
                                          setServices((prev) =>
                                            prev.map((x) =>
                                              x.id === s.id
                                                ? {
                                                    ...x,
                                                    addons: x.addons.map((y) =>
                                                      y.id === a.id ? { ...y, deleted: true } : y,
                                                    ),
                                                  }
                                                : x,
                                            ),
                                          )
                                        }
                                        className="h-6 w-6 flex items-center justify-center text-red-500 bg-red-50 active:bg-red-100 rounded-full active:scale-95 transition-all flex-shrink-0"
                                        style={{ WebkitTapHighlightColor: 'transparent' }}
                                      >
                                        <Trash2 className="h-3 w-3" />
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Newly added services */}
              {newServices.length > 0 && (
                <div className="bg-white rounded-[12px] border border-blue-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                  <div className="bg-blue-50/50 px-3 py-2 border-b border-blue-200/60">
                    <span className="text-[11px] font-semibold text-blue-700 uppercase tracking-wide">New services</span>
                  </div>
                  <div className="divide-y divide-gray-200/60">
                    {newServices.map((s) => (
                      <div key={s.key} className="flex items-start justify-between px-3 py-2.5">
                        <div className="flex-1 min-w-0 pr-3">
                          <span className="text-[14px] font-semibold text-gray-900" style={appleFont}>{s.name}</span>
                          {s.description && (
                            <RichTextDescription
                              description={s.description}
                              className="text-[12px] text-gray-600 mt-1 leading-relaxed"
                            />
                          )}
                          <div className="mt-2.5">
                            <button
                              onClick={() => setNewServices((prev) => prev.filter((x) => x.key !== s.key))}
                              className="px-3 py-1.5 bg-red-50 text-red-600 rounded-[8px] text-[11px] font-medium active:bg-red-200 active:scale-95 transition-all duration-150 touch-manipulation"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                        <input
                          type="number"
                          inputMode="decimal"
                          className="w-[84px] text-right text-[15px] font-semibold text-gray-900 bg-gray-50 border border-gray-200 rounded-[8px] px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                          value={s.price}
                          min={0}
                          step="0.01"
                          onChange={(e) =>
                            setNewServices((prev) =>
                              prev.map((x) => (x.key === s.key ? { ...x, price: Number(e.target.value) || 0 } : x)),
                            )
                          }
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add Service — opens the same SelectServiceModal as the booking info modal */}
              <button
                onClick={() => setIsSelectServiceOpen(true)}
                className="w-full flex items-center justify-center px-3 py-2.5 bg-white border border-gray-200/60 rounded-[10px] text-[13px] font-medium text-gray-700 active:bg-gray-100 transition-all duration-150 active:scale-[0.98] touch-manipulation shadow-[0_1px_2px_rgba(0,0,0,0.05)]"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <Plus className="h-4 w-4 mr-1.5" />
                Add Service
              </button>

              {/* ── Products — same visibility criteria as the booking info modal ── */}
              {productsRelevant && (
                <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                  <div className="bg-gray-50/50 px-3 py-2.5 border-b border-gray-200/60">
                    <span className="text-[13px] font-semibold text-gray-900" style={appleFont}>Products</span>
                  </div>
                  <div className="divide-y divide-gray-200/60">
                    {products.filter((p) => !p.deleted).length === 0 && newProducts.length === 0 && (
                      <p className="text-[13px] text-gray-400 px-3 py-3 text-center">No products</p>
                    )}
                    {products.map((p) =>
                      p.deleted ? null : (
                        <div key={p.id} className="flex items-center gap-3 px-3 py-2.5">
                          <div className="h-10 w-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
                            <Package2 className="h-5 w-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-[13px] font-semibold text-gray-900 truncate block" style={appleFont}>
                              {p.name}
                            </span>
                            <div className="flex items-center gap-1.5 mt-1">
                              <span className="text-[11px] text-gray-500">Qty</span>
                              <input
                                type="number"
                                inputMode="numeric"
                                className="w-14 text-right text-[12px] bg-gray-50 border border-gray-200 rounded-[6px] px-1.5 py-0.5"
                                value={p.quantity}
                                min={1}
                                onChange={(e) =>
                                  setProducts((prev) =>
                                    prev.map((x) =>
                                      x.id === p.id
                                        ? { ...x, quantity: Math.max(Number(e.target.value) || 1, 1) }
                                        : x,
                                    ),
                                  )
                                }
                              />
                              <button
                                onClick={() =>
                                  setProducts((prev) =>
                                    prev.map((x) => (x.id === p.id ? { ...x, deleted: true } : x)),
                                  )
                                }
                                className="ml-1 px-3 py-1.5 bg-red-50 text-red-600 rounded-[8px] text-[11px] font-medium active:bg-red-200 active:scale-95 transition-all duration-150 touch-manipulation"
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                          <input
                            type="number"
                            inputMode="decimal"
                            className={smallPriceInput}
                            value={p.unit_price}
                            min={0}
                            step="0.01"
                            onChange={(e) =>
                              setProducts((prev) =>
                                prev.map((x) =>
                                  x.id === p.id ? { ...x, unit_price: Number(e.target.value) || 0 } : x,
                                ),
                              )
                            }
                          />
                        </div>
                      ),
                    )}
                    {newProducts.map((p) => (
                      <div key={p.key} className="flex items-center gap-3 px-3 py-2.5 bg-blue-50/30">
                        <div className="h-10 w-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
                          <Package2 className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="text-[13px] font-semibold text-gray-900 truncate block" style={appleFont}>
                            {p.name} <span className="text-[10px] text-blue-600 font-semibold">NEW</span>
                          </span>
                          <div className="flex items-center gap-1.5 mt-1">
                            <span className="text-[11px] text-gray-500">Qty</span>
                            <input
                              type="number"
                              inputMode="numeric"
                              className="w-14 text-right text-[12px] bg-gray-50 border border-gray-200 rounded-[6px] px-1.5 py-0.5"
                              value={p.quantity}
                              min={1}
                              onChange={(e) =>
                                setNewProducts((prev) =>
                                  prev.map((x) =>
                                    x.key === p.key
                                      ? { ...x, quantity: Math.max(Number(e.target.value) || 1, 1) }
                                      : x,
                                  ),
                                )
                              }
                            />
                            <button
                              onClick={() => setNewProducts((prev) => prev.filter((x) => x.key !== p.key))}
                              className="ml-1 px-3 py-1.5 bg-red-50 text-red-600 rounded-[8px] text-[11px] font-medium active:bg-red-200 active:scale-95 transition-all duration-150 touch-manipulation"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                        <input
                          type="number"
                          inputMode="decimal"
                          className={smallPriceInput}
                          value={p.unit_price}
                          min={0}
                          step="0.01"
                          onChange={(e) =>
                            setNewProducts((prev) =>
                              prev.map((x) =>
                                x.key === p.key ? { ...x, unit_price: Number(e.target.value) || 0 } : x,
                              ),
                            )
                          }
                        />
                      </div>
                    ))}
                  </div>
                  {/* Add Product — dashed button, matching BookingProductList */}
                  {isInventoryAppInstalled && showInventoryInBookings && (
                    <div className="p-3 border-t border-gray-200/60">
                      <button
                        onClick={() => setShowAddProduct((v) => !v)}
                        className="w-full flex items-center justify-center px-3 py-2 border border-dashed border-gray-300 rounded-lg text-sm font-medium text-gray-600 active:bg-gray-50 transition-colors touch-manipulation"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Product
                      </button>
                      {showAddProduct && (
                        <div className="mt-1.5 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                          <select
                            className="w-full text-[13px] bg-white px-3 py-2.5 focus:outline-none"
                            defaultValue=""
                            onChange={(e) => {
                              const item = productCatalog.find((x) => x.id === e.target.value);
                              if (!item) return;
                              setNewProducts((prev) => [
                                ...prev,
                                {
                                  key: `${item.id}-${Date.now()}`,
                                  stock_item_id: item.id,
                                  name: item.name,
                                  quantity: 1,
                                  unit_price: item.price,
                                },
                              ]);
                              setShowAddProduct(false);
                              e.target.value = '';
                            }}
                          >
                            <option value="" disabled>Choose a product…</option>
                            {productCatalog.map((p) => (
                              <option key={p.id} value={p.id}>{p.name} — {money(p.price)}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ── Booking-level discount — $/% segmented control like the booking modal ── */}
              <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                <div className="bg-gray-50/50 px-3 py-2.5 border-b border-gray-200/60 flex items-center justify-between">
                  <span className="text-[13px] font-semibold text-gray-900" style={appleFont}>Booking Discount</span>
                  {!discount && (
                    <button
                      onClick={() => {
                        setDiscount({ type: 'percentage', amount: 0, title: 'Discount' });
                        setDiscountDirty(true);
                      }}
                      className="inline-flex items-center gap-1 text-[12px] font-semibold text-blue-600 touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <Plus className="h-3.5 w-3.5" /> Add
                    </button>
                  )}
                </div>
                {discount ? (
                  <div className="p-3">
                    <div className="flex items-center gap-2">
                      <div className="flex bg-white border border-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => {
                            setDiscount((d) => (d ? { ...d, type: 'fixed' } : d));
                            setDiscountDirty(true);
                          }}
                          className={`px-2.5 py-1.5 text-[12px] font-semibold ${discount.type === 'fixed' ? 'bg-blue-600 text-white' : 'text-gray-600'}`}
                        >
                          $
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setDiscount((d) => (d ? { ...d, type: 'percentage' } : d));
                            setDiscountDirty(true);
                          }}
                          className={`px-2.5 py-1.5 text-[12px] font-semibold ${discount.type === 'percentage' ? 'bg-blue-600 text-white' : 'text-gray-600'}`}
                        >
                          %
                        </button>
                      </div>
                      <input
                        type="number"
                        inputMode="decimal"
                        min={0}
                        step="0.01"
                        value={discount.amount}
                        onChange={(e) => {
                          setDiscount((d) => (d ? { ...d, amount: Number(e.target.value) || 0 } : d));
                          setDiscountDirty(true);
                        }}
                        placeholder={discount.type === 'percentage' ? 'e.g. 10' : 'e.g. 25.00'}
                        className="flex-1 min-w-0 px-2.5 py-1.5 text-[13px] bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                        style={{ fontSize: '16px' }}
                      />
                      <button
                        onClick={() => {
                          setDiscount(null);
                          setDiscountDirty(true);
                        }}
                        className="h-7 w-7 flex items-center justify-center text-red-500 bg-red-50 active:bg-red-100 rounded-full active:scale-95 transition-all flex-shrink-0"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-[13px] text-gray-400 px-3 py-3">No booking discount</p>
                )}
              </div>

              {/* Reason (optional) */}
              <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                <div className="bg-gray-50/50 px-3 py-2.5 border-b border-gray-200/60">
                  <span className="text-[13px] font-semibold text-gray-900" style={appleFont}>Reason (optional)</span>
                </div>
                <div className="p-3">
                  <input
                    type="text"
                    className="w-full text-[13px] bg-gray-50 border border-gray-200 rounded-[8px] px-3 py-2 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    placeholder="e.g. Forgot to add ceramic coating"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    maxLength={200}
                    style={{ fontSize: '16px' }}
                  />
                </div>
              </div>

              {error && (
                <div className="rounded-[12px] bg-red-50 border border-red-100 px-3 py-2.5 text-[13px] text-red-700">
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!loading && billing && (
          <div
            className="bg-white border-t border-gray-100 px-5 pt-3 shrink-0"
            style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
          >
            <div className="flex items-center justify-between text-[13px] mb-1">
              <span className="text-gray-500">Current total</span>
              <span className="font-medium text-gray-900 tabular-nums">{money(currentTotal)}</span>
            </div>
            <div className="flex items-center justify-between text-[13px] mb-1">
              <span className="text-gray-500">New subtotal (before tax)</span>
              <span className="font-semibold text-gray-900 tabular-nums">{money(estimatedSubtotal)}</span>
            </div>
            {paidSoFar > 0 && (
              <div className="flex items-center justify-between text-[13px] mb-1">
                <span className="text-gray-500">Paid so far</span>
                <span className="font-medium text-emerald-600 tabular-nums">{money(paidSoFar)}</span>
              </div>
            )}
            <p className="text-[11px] text-gray-400 mb-3">Tax is recalculated automatically when you save.</p>
            <button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className="w-full py-3 rounded-[12px] bg-gray-900 text-white text-[15px] font-semibold disabled:opacity-40 active:scale-[0.99] transition-transform touch-manipulation flex items-center justify-center gap-2"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {saving ? 'Amending…' : 'Save & Re-issue Invoice'}
            </button>
          </div>
        )}
      </div>
    </div>

    {/* Same service picker as the booking info modal — it portals itself to
        document.body above this panel. The pick is STAGED (not written) until
        Save, so the amend stays atomic. Rendered outside the overlay so its
        clicks don't bubble into the close-on-backdrop handler. */}
    {isSelectServiceOpen && (
        <SelectServiceModal
          isOpen={isSelectServiceOpen}
          onClose={() => setIsSelectServiceOpen(false)}
          onAdd={async (service: any, priceOption: any) => {
            const pricingType = priceOption?.pricing_type || 'flat';
            const isPerUnit = pricingType === 'per_unit' || pricingType === 'per_sqft';
            setNewServices((prev) => [
              ...prev,
              {
                key: `${service.id}-${Date.now()}`,
                service_id: service.id,
                name: service.name,
                description: service.description || '',
                price: isPerUnit ? 0 : Number(priceOption?.price) || 0,
                quantity: 1,
                vehicle_id: singleVehicleId,
                tax_exempt: service.tax_exempt || false,
                tax_rate: service.tax_exempt ? 0 : 5,
                pricing_type: pricingType,
                unit_price: isPerUnit ? Number(priceOption?.unit_price) || 0 : undefined,
                duration_per_unit: isPerUnit ? Number(priceOption?.duration_per_unit) || 0 : undefined,
                duration_minutes: isPerUnit ? 0 : Number(priceOption?.duration_minutes) || 0,
              },
            ]);
            setIsSelectServiceOpen(false);
          }}
        />
      )}
    </>,
    document.body,
  );
}
